// C:\Users\server\Desktop\FRONTEND\app-user-front\src\pages\Dashboard\Productpage.js
// ==================== Imports ====================
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Row, Col, Nav, NavItem, NavLink, Spinner } from 'react-bootstrap';
import axios from 'axios';
import RequestModal from './requestmodel'; // Import the separated RequestModal
import CONFIG from '../../config';

// ==================== Helper: API origin + getImageUrl ====================
const API_ORIGIN = (CONFIG.API_URL || '').replace(/\/$/, '');

/**
 * Accepts either a product object or a string path and returns an absolute URL or null.
 * - If input is product object with product.image, uses that.
 * - If input is string, uses that as image path.
 */
function getImageUrl(input) {
  let img = null;

  if (!input) return null;

  if (typeof input === 'object') {
    img = input.image;
  } else if (typeof input === 'string') {
    img = input;
  }

  if (!img || typeof img !== 'string') return null;

  const trimmed = img.trim();
  if (!trimmed) return null;

  if (/^https?:\/\//i.test(trimmed)) return trimmed; // full URL already
  if (trimmed.startsWith('/')) return `${API_ORIGIN}${trimmed}`; // absolute path on server
  return `${API_ORIGIN}/${trimmed}`; // relative path -> prefix origin
}

// ------------------ Helper: Ensure timestamp and sorting ------------------
function ensureTimestamp(prod) {
  if (!prod) return prod;
  const ts =
    prod.created_at ||
    prod.createdAt ||
    prod.createdAtTimestamp ||
    prod._created_at ||
    prod.created ||
    null;
  return { ...prod, created_at: ts || Date.now() };
}

function sortNewestFirst(a, b) {
  const ta = Number(new Date(a?.created_at || a?.createdAt || 0)) || 0;
  const tb = Number(new Date(b?.created_at || b?.createdAt || 0)) || 0;
  return tb - ta; // descend: newest first
}

// ---------- Helper: only show admin-approved products ----------
function isApprovedProduct(product) {
  const s = (product?.approval_status || '').toString().toLowerCase();
  return s === 'accepted';
}

// ---------------- STAR RATING COMPONENT ----------------
const StarRating = ({ rating }) => {
  const totalStars = 5;
  const numericRating = parseFloat(rating) || 0;

  const fullStar = (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="url(#starGradient)"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="starGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" style={{ stopColor: "#FFD700", stopOpacity: 1 }} />
          <stop offset="100%" style={{ stopColor: "#FFA500", stopOpacity: 1 }} />
        </linearGradient>
      </defs>
      <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27z" />
    </svg>
  );

  const halfStar = (
    <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27z"
        fill="#E0E0E0"
      />
      <path
        d="M12 17.27V2L9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27z"
        fill="url(#starGradient)"
      />
    </svg>
  );

  const emptyStar = (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="#E0E0E0"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27z" />
    </svg>
  );

  const stars = [];
  for (let i = 1; i <= totalStars; i++) {
    if (i <= numericRating) stars.push(<span key={`star-${i}`}>{fullStar}</span>);
    else if (i - 0.5 <= numericRating) stars.push(<span key={`star-${i}`}>{halfStar}</span>);
    else stars.push(<span key={`star-${i}`}>{emptyStar}</span>);
  }
  return <div className="star-rating-display">{stars}</div>;
};

// ---------------- PRODUCT CARD COMPONENT ----------------
const ProductCard = ({ product, index, onProductClick }) => {
  const [imgSrc, setImgSrc] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const preloaderRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setImageLoaded(false);

    const resolved = getImageUrl(product) || '/images/default-product.jpg';

    // Cleanup any previous preloader
    if (preloaderRef.current) {
      preloaderRef.current.onload = null;
      preloaderRef.current.onerror = null;
      preloaderRef.current = null;
    }

    const img = new Image();
    preloaderRef.current = img;

    img.onload = () => {
      if (!cancelled) {
        setImgSrc(resolved);
        setImageLoaded(true);
      }
    };
    img.onerror = () => {
      if (!cancelled) {
        setImgSrc('/images/default-product.jpg');
        setImageLoaded(true);
        console.warn(`ProductCard: failed to load ${resolved}`);
      }
    };

    // start loading
    img.src = resolved;

    return () => {
      cancelled = true;
      if (preloaderRef.current) {
        preloaderRef.current.onload = null;
        preloaderRef.current.onerror = null;
        preloaderRef.current = null;
      }
    };
  }, [product]);

  return (
    <Col xs={12} sm={6} md={4} lg={3} className="mb-4 product-item-animate" style={{ animationDelay: `${index * 0.05}s` }}>
      <div
        className="card explore-box card-animate"
        style={{ cursor: 'pointer', ...styles.productCard }}
        onClick={() => onProductClick(product)}
      >
        <div className="explore-place-bid-img">
          {!imageLoaded && (
            <div style={styles.imageLoading}>
              <Spinner animation="border" size="sm" />
              <span>Loading...</span>
            </div>
          )}
          {imgSrc ? (
            <img
              src={imgSrc}
              alt={product.name}
              className="card-img-top explore-img"
              style={{
                ...styles.productImage,
                opacity: imageLoaded ? 1 : 0
              }}
              loading="lazy"
            />
          ) : (
            <div style={{ ...styles.productImage, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6c757d' }}>
              No Image
            </div>
          )}
          <div className="bg-overlay"></div>
          <div className="place-bid-btn">
            <span className="btn btn-success">
              <i className="ri-eye-fill align-bottom me-1"></i> View Details
            </span>
          </div>
        </div>
        <div className="card-body">
          <p className="fw-medium mb-0 float-end">
            <i className="mdi mdi-heart text-danger align-middle"></i> {product.rating || "0"}
          </p>
          <h5 className="mb-1 text-truncate">{product.name}</h5>
          <p className="text-muted mb-0">{product.category || "Uncategorized"}</p>
        </div>
        <div className="card-footer border-top border-top-dashed">
          <div className="d-flex align-items-center">
            <div className="flex-grow-1 fs-14">
              <i className="ri-price-tag-3-fill text-warning align-bottom me-1"></i>
              Available for Swap
            </div>
          </div>
        </div>
      </div>
    </Col>
  );
};

// ---------------- PREMIUM MODAL ----------------
const PremiumModal = ({ isOpen, onClose, product, onRequestClick }) => {
  const navigate = useNavigate();
  const [productImg, setProductImg] = useState('');
  const [imageLoaded, setImageLoaded] = useState(false);
  const preloaderRef = useRef(null);

  useEffect(() => {
    if (isOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "unset";

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // ==================== Handle Image Loading ====================
  useEffect(() => {
    if (!product) return;
    setImageLoaded(false);

    const resolved = getImageUrl(product) || '/images/default-product.jpg';

    // Cleanup previous preloader if present
    if (preloaderRef.current) {
      preloaderRef.current.onload = null;
      preloaderRef.current.onerror = null;
      preloaderRef.current = null;
    }

    const img = new Image();
    preloaderRef.current = img;

    let cancelled = false;
    img.onload = () => {
      if (!cancelled) {
        setProductImg(resolved);
        setImageLoaded(true);
      }
    };
    img.onerror = () => {
      if (!cancelled) {
        setProductImg('/images/default-product.jpg');
        setImageLoaded(true);
        console.warn(`PremiumModal: failed to load ${resolved}`);
      }
    };
    img.src = resolved;

    return () => {
      cancelled = true;
      if (preloaderRef.current) {
        preloaderRef.current.onload = null;
        preloaderRef.current.onerror = null;
        preloaderRef.current = null;
      }
    };
  }, [product]);

  const handleNavigate = () => {
    navigate(`/product/${product.id}`);
    onClose(); // This closes the modal
  };

  const handleMakeOfferClick = () => {
    onRequestClick(product);
    onClose();
  };

  if (!isOpen || !product) return null;

  return (
    <div className="premium-modal-overlay" onClick={onClose}>
      <div className="premium-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="premium-modal-close-btn" onClick={onClose} title="Close">&times;</button>
        <div className="premium-modal-body-grid">
          <div className="premium-modal-img-container">
            {!imageLoaded && productImg && (
              <div style={styles.modalImageLoading}>
                <Spinner animation="border" />
                <span>Loading image...</span>
              </div>
            )}
            <img
              src={productImg}
              alt={product.name}
              className="premium-modal-img"
              style={{
                opacity: imageLoaded ? 1 : 0
              }}
            />
          </div>
          <div className="premium-modal-details">
            <p className="premium-modal-category">{product.category}</p>
            <h2 className="premium-modal-title">{product.name}</h2>
            <p className="premium-modal-description">{product.description || "No detailed description is available for this product."}</p>

            <div className="premium-modal-info-grid">
              <div className="info-item">
                <strong>Status:</strong>
                <span className="status-value available">Available for Swap</span>
              </div>
              <div className="info-item">
                <strong>Category:</strong>
                <span className="category-value">{product.category || "Uncategorized"}</span>
              </div>
            </div>

            <div className="premium-modal-footer">
              <button className="premium-btn btn-primary-premium" onClick={handleNavigate}>
                <i className="ri-eye-line me-2"></i> View Details
              </button>
              <button
                className="premium-btn btn-warning-premium"
                onClick={handleMakeOfferClick}
                style={{ flex: 2 }}
              >
                <i className="ri-send-plane-line me-2"></i> Make an Offer
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------- PRODUCT PAGE COMPONENT ----------------
// Accept props: limit (default 8) and showAllOpensPage (default false)
const ProductPage = ({ limit = 8, showAllOpensPage = false, headerText = "Our Products", headerSubtitle = "" }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // detect if we're on the full products page (hide button in that case)
  const isProductsRoute = location.pathname === '/products' || location.pathname.startsWith('/products');

  // embedded (home) = not products route
  const isEmbedded = !isProductsRoute;

  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [premiumModalOpen, setPremiumModalOpen] = useState(false);
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState("All");

  // NEW: control showAll and default limit (from prop)
  const [showAll, setShowAll] = useState(false);
  const DEFAULT_LIMIT = limit;

  // 🔝 Scroll to top on mount (when someone visits this page)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // ✅ Read category from URL query (?category=) – for footer links, shareable links, etc.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const categoryFromUrl = params.get("category");

    if (categoryFromUrl && categoryFromUrl !== activeCategory) {
      setActiveCategory(categoryFromUrl);
      setShowAll(true); // On /products?category=... show all of that category
    }
  }, [location.search, activeCategory]);

  // ==================== Fetch products from your API ====================
  useEffect(() => {
    let mounted = true;
    const fetchProducts = async () => {
      try {
        const response = await fetch(`${API_ORIGIN}/products`);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        // Handle different response formats
        let items = [];
        if (Array.isArray(result)) {
          items = result;
        } else if (result.products && Array.isArray(result.products)) {
          items = result.products;
        } else if (result.data && Array.isArray(result.data)) {
          items = result.data;
        } else {
          console.warn('Unexpected API response format:', result);
          items = [];
        }

        // Normalize image field to absolute URL or null, ensure timestamp
        const normalized = (items || []).map(p => {
          const imageStr = (p.image && typeof p.image === 'string') ? p.image.trim() : '';
          return ensureTimestamp({
            ...p,
            image: imageStr ? getImageUrl(imageStr) : null
          });
        }).sort(sortNewestFirst);

        if (mounted) {
          setProducts(normalized);
          setProductsLoading(false);
        }
      } catch (err) {
        console.error('Failed to fetch products:', err);
        if (mounted) {
          setProductsLoading(false);
          setProducts([]); // Set empty array on error
        }
      }
    };

    fetchProducts();

    return () => {
      mounted = false;
    };
  }, []);

  // ---------- LISTEN FOR product-updated / product-added EVENTS ----------
  // So new/updated products show on top immediately (keeps consistent with Userpage)
  useEffect(() => {
    const onProdUpdated = (e) => {
      const prod = e?.detail;
      if (!prod) return;
      const normalized = ensureTimestamp({
        ...prod,
        image: getImageUrl(prod.image || prod.main_image || prod.image_full_url || prod)
      });
      setProducts((prev) =>
        prev
          .map((p) => (String(p.id || p._id) === String(normalized.id || normalized._id) ? { ...p, ...normalized } : p))
          .sort(sortNewestFirst)
      );
    };

    const onProdAdded = (e) => {
      const prod = e?.detail;
      if (!prod) return;
      const normalized = ensureTimestamp({
        ...prod,
        image: getImageUrl(prod.image || prod.main_image || prod.image_full_url || prod)
      });
      setProducts((prev) => [normalized, ...prev].sort(sortNewestFirst));
    };

    window.addEventListener('product-updated', onProdUpdated);
    window.addEventListener('product-added', onProdAdded);

    return () => {
      window.removeEventListener('product-updated', onProdUpdated);
      window.removeEventListener('product-added', onProdAdded);
    };
  }, []);

  // ==================== Product Handlers ====================
  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setPremiumModalOpen(true);
  };

  const handleRequestClick = (product) => {
    setSelectedProduct(product);
    setRequestModalOpen(true);
  };

  const handleRequestSubmit = async (requesterEmail, productId) => {
    try {
      const response = await axios.post(`${API_ORIGIN}/swap-requests`, {
        requester_email: requesterEmail,
        product_id: productId
      });

      if (response.data.message) {
        alert('✅ ' + response.data.message);
      } else {
        alert('✅ Request sent successfully!');
      }
    } catch (error) {
      console.error('Error sending swap request:', error);

      if (error.response?.data?.message) {
        alert('❌ ' + error.response.data.message);
      } else if (error.response?.data?.errors) {
        const errors = Object.values(error.response.data.errors).flat().join('\n');
        alert('❌ ' + errors);
      } else {
        alert('❌ Failed to send request. Please try again.');
      }
      throw error;
    }
  };

  const togglePremiumModal = () => setPremiumModalOpen(!premiumModalOpen);
  const toggleRequestModal = () => setRequestModalOpen(!requestModalOpen);

  const handleNavClick = (category) => {
    setActiveCategory(category);
    setShowAll(false);

    // ✅ Sync URL with selected category on /products
    if (isProductsRoute) {
      const params = new URLSearchParams(location.search);
      if (category === "All") {
        params.delete("category");
      } else {
        params.set("category", category);
      }
      const search = params.toString();
      navigate({
        pathname: location.pathname,
        search: search ? `?${search}` : ''
      }, { replace: true });
    }
  };

  // 🔴 MAIN CHANGE: only approved products are allowed into filtered list
  const filteredProducts = products.filter((product) => {
    if (!isApprovedProduct(product)) return false; // only admin approved
    if (activeCategory === "All") return true;
    return activeCategory.toLowerCase() === product.category?.toLowerCase();
  });

  // FIX: when on the full products route, show all filtered products (don't slice)
  const displayedProducts = (isProductsRoute || showAll) ? filteredProducts : filteredProducts.slice(0, DEFAULT_LIMIT);

  return (
    <>
      {/* Global font import for headings (same as Home.js) */}
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@600;700;800&display=swap');
        `}
      </style>

      {/* HEADER: big hero on products route, compact white header when embedded (home) */}
      {isEmbedded ? (
        <section style={styles.simpleHero} aria-label="Our products simple header">
          <Container>
            <Row className="align-items-center">
              <Col xs={12} md={8} className="mx-auto text-center">
                <h2 style={styles.simpleHeroTitle}>{headerText}</h2>
                {headerSubtitle ? <p style={styles.simpleHeroSubtitle}>{headerSubtitle}</p> : null}
              </Col>
            </Row>
          </Container>
        </section>
      ) : (
        <section style={styles.hero} aria-label="Products hero">
          <h1 style={styles.heroTitle}>{headerText}</h1>
          <p style={styles.heroDescription}>
            {activeCategory === "All"
              ? "Discover amazing products from our collection. Filter by category to find what you're looking for."
              : `Showing products in "${activeCategory}" category. Explore more items you can swap and enjoy.`}
          </p>
        </section>
      )}

      <section className="section" id="products" style={styles.productsSection}>
        <Container>
          <Row className="justify-content-center">
            <Col lg={8}>
              <div className="text-center mb-5">
                <Nav pills className="nav-pills filter-btns justify-content-center" role="tablist">
                  <NavItem>
                    <NavLink
                      type="button"
                      onClick={() => handleNavClick("All")}
                      className={activeCategory === "All" ? "fw-medium active" : "fw-medium"}
                      style={{
                        ...styles.navLink,
                        color: activeCategory === "All" ? "#fff" : "#702963"
                      }}
                    >
                      All Products
                    </NavLink>
                  </NavItem>
                  {["Toys & Games", "Sporting Goods", "Accessories", "Clothes", "Shoes", "Books", "Bags"].map((cat) => (
                    <NavItem key={cat}>
                      <NavLink
                        type="button"
                        onClick={() => handleNavClick(cat)}
                        className={activeCategory === cat ? "fw-medium active" : "fw-medium"}
                        style={{
                          ...styles.navLink,
                          color: activeCategory === cat ? "#fff" : "#702963"
                        }}
                      >
                        {cat}
                      </NavLink>
                    </NavItem>
                  ))}
                </Nav>
              </div>
            </Col>
          </Row>

          <Row key={activeCategory}>
            {productsLoading ? (
              <Col className="text-center">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2">Loading products...</p>
              </Col>
            ) : filteredProducts.length === 0 ? (
              <Col className="text-center">
                <p className="text-muted">No products found in this category.</p>
              </Col>
            ) : (
              displayedProducts.map((product, index) => (
                <ProductCard
                  key={product.id || product._id || index}
                  product={product}
                  index={index}
                  onProductClick={handleProductClick}
                />
              ))
            )}
          </Row>

          {/* Show all / Show less button */}
          {!productsLoading && filteredProducts.length > DEFAULT_LIMIT && !isProductsRoute && (
            <Row>
              <Col className="text-center">
                <button
                  onClick={() => {
                    // If embedded (home), always navigate to /products (main page where button works)
                    if (isEmbedded) {
                      navigate('/products');
                      return;
                    }

                    // Otherwise (on /products) toggle show all
                    if (showAllOpensPage) {
                      navigate('/products');
                    } else {
                      setShowAll(prev => !prev);
                    }
                  }}
                  className="btn"
                  style={{
                    padding: '10px 22px',
                    borderRadius: 8,
                    border: showAll ? '1px solid #702963' : 'none',
                    backgroundColor: showAll ? 'transparent' : '#702963',
                    color: showAll ? '#702963' : '#fff',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: 15,
                    marginTop: 20
                  }}
                >
                  {showAllOpensPage && isEmbedded
                    ? `View all (${filteredProducts.length})`
                    : (showAll ? 'Show less' : `Show all (${filteredProducts.length})`)}
                </button>
              </Col>
            </Row>
          )}
        </Container>
      </section>

      {/* Premium Modal */}
      <PremiumModal
        isOpen={premiumModalOpen}
        onClose={togglePremiumModal}
        product={selectedProduct}
        onRequestClick={handleRequestClick}
      />

      {/* Request Modal - Separated Component */}
      <RequestModal
        show={requestModalOpen}
        onClose={toggleRequestModal}
        product={selectedProduct}
        onRequestSubmit={handleRequestSubmit}
      />

      {/* Embedded Styles */}
      <style>{`
        .product-item-animate { 
          animation: fadeInUp 0.5s ease-out forwards; 
          opacity: 0; 
        }
        
        @keyframes fadeInUp { 
          from { 
            opacity:0; 
            transform:translateY(30px);
          } 
          to { 
            opacity:1; 
            transform:translateY(0); 
          } 
        }
        
        .premium-modal-overlay { 
          position:fixed; 
          top:0; 
          left:0; 
          right:0; 
          bottom:0; 
          background:rgba(248,249,250,0.7); 
          display:flex; 
          align-items:center; 
          justify-content:center; 
          z-index:1050; 
          backdrop-filter:blur(8px); 
          -webkit-backdrop-filter:blur(8px); 
          animation: modalFadeIn 0.4s ease-out; 
        }
        
        .premium-modal-content { 
          background:#fff; 
          color:#343a40; 
          padding:2.5rem 3rem; 
          border-radius:16px; 
          border:1px solid #e9ecef; 
          box-shadow:0 25px 50px -12px rgba(0,0,0,0.15); 
          width:90%; 
          max-width:950px; 
          position:relative; 
          animation: modalSlideUp 0.5s cubic-bezier(0.16,1,0.3,1);
        }
        
        .premium-modal-close-btn { 
          position:absolute; 
          top:15px; 
          right:15px; 
          background:#f1f1f1; 
          border:none; 
          width:36px; 
          height:36px; 
          border-radius:50%; 
          font-size:22px; 
          line-height:36px; 
          text-align:center; 
          cursor:pointer; 
          color:#555; 
          transition: all 0.2s ease; 
        }
        
        .premium-modal-close-btn:hover { 
          background:#e0e0e0; 
          color:#000; 
        }
        
        .premium-modal-body-grid { 
          display:grid; 
          grid-template-columns:0.8fr 1.2fr; 
          gap:3rem; 
          align-items:center; 
        }
        
        .premium-modal-img-container { 
          width:100%; 
          border-radius:12px; 
          overflow:hidden; 
          position: relative;
          background: #f8f9fa;
          min-height: 300px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .premium-modal-img { 
          width:100%; 
          height:300px; 
          object-fit:cover; 
          display:block; 
          transition: opacity 0.3s ease;
        }
        
        .premium-modal-details { 
          display:flex; 
          flex-direction:column; 
          height:100%; 
        }
        
        .premium-modal-category { 
          text-transform:uppercase; 
          color:#0056b3; 
          font-size:0.85rem; 
          font-weight:700; 
          letter-spacing:1px; 
          margin-bottom:0.5rem; 
        }
        
        .premium-modal-title { 
          font-size:2rem; 
          font-weight:600; 
          color:#212529; 
          marginBottom:1rem; 
          line-height:1.2; 
        }
        
        .premium-modal-description { 
          color:#6c757d; 
          font-size:0.95rem; 
          line-height:1.7; 
          margin-bottom:1.5rem; 
          flex-grow: 1;
        }

        .premium-modal-info-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
          margin-bottom: 1.5rem;
          padding: 1rem;
          background: #f8f9fa;
          border-radius: 8px;
        }

        .info-item {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .info-item strong {
          font-size: 0.85rem;
          color: #6c757d;
        }

        .status-value {
          font-size: 1rem;
          font-weight: 700;
        }

        .status-value.available {
          color: #198754;
        }

        .category-value {
          font-size: 0.9rem;
          font-weight: 600;
          color: #0056b3;
        }
        
        .premium-modal-footer { 
          display:flex; 
          gap:1rem; 
          padding-top:1.5rem; 
          margin-top:1rem; 
          border-top:1px solid #e9ecef; 
        }
        
        .premium-btn { 
          border:none; 
          padding:0.9rem 1.5rem; 
          border-radius:8px; 
          font-size:1rem; 
          font-weight:600; 
          cursor:pointer; 
          transition: all 0.2s ease; 
          position:relative; 
          overflow:hidden; 
          display:flex; 
          align-items:center; 
          justify-content:center; 
          flex: 1;
        }
        
        .premium-btn:not(:disabled):hover { 
          transform: translateY(-3px); 
          box-shadow: 0 8px 20px rgba(0,0,0,0.1); 
        }
        
        .premium-btn::before { 
          content: ''; 
          position:absolute; 
          top:0; 
          left:-100%; 
          width:100%; 
          height:100%; 
          background: linear-gradient(120deg, transparent, rgba(255,255,255,0.4), transparent); 
          transition:left 0.6s ease; 
        }
        
        .premium-btn:hover::before { 
          left:100%; 
        }
        
        .premium-btn i { 
          vertical-align: middle; 
          line-height: 1; 
        }
        
        .premium-btn:disabled { 
          opacity:0.7; 
          cursor:not-allowed; 
        }
        
        .btn-primary-premium { 
          background: linear-gradient(45deg,#0d6efd,#0056b3); 
          color:#fff; 
        }

        .btn-warning-premium { 
          background: linear-gradient(45deg,#ffc107,#fd7e14); 
          color:#fff; 
        }
        
        @keyframes modalFadeIn { 
          from { opacity:0; } 
          to { opacity:1; } 
        }
        
        @keyframes modalSlideUp { 
          from { 
            opacity:0; 
            transform: translateY(50px) scale(0.95); 
          } 
          to { 
            opacity:1; 
            transform: translateY(0) scale(1); 
          } 
        }
        
        @media (max-width:768px) {
          .premium-modal-content { padding:1.5rem; }
          .premium-modal-body-grid { grid-template-columns:1fr; gap:1.5rem; }
          .premium-modal-title { font-size:1.7rem; }
          .premium-modal-info-grid { grid-template-columns: 1fr; }
          .premium-modal-footer { flex-direction: column; }
          .premium-btn { width:100%; }
        }

        .explore-place-bid-img {
          position: relative;
          overflow: hidden;
          height: 200px;
          background: #f8f9fa;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .bg-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.3);
          opacity: 0;
          transition: all 0.3s ease;
        }

        .place-bid-btn {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%) scale(0.8);
          opacity: 0;
          transition: all 0.3s ease;
        }

        .explore-place-bid-img:hover .bg-overlay {
          opacity: 1;
        }

        .explore-place-bid-img:hover .place-bid-btn {
          opacity: 1;
          transform: translate(-50%, -50%) scale(1);
        }

        .nav-pills .nav-link.active {
          background-color: #FF7F50;
          color: white;
        }
      `}</style>
    </>
  );
};

// ==================== Styles ====================
const styles = {
  productsSection: {
    marginTop: '40px',
    padding: '20px 0',
    minHeight: '70vh',
  },
  productCard: {
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    border: 'none',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    borderRadius: '12px',
    overflow: 'hidden'
  },
  productImage: {
    height: '200px',
    objectFit: 'cover',
    width: '100%',
    transition: 'opacity 0.3s ease'
  },
  navLink: {
    cursor: 'pointer',
    margin: '0 5px',
    borderRadius: '20px',
    padding: '8px 12px'
  },
  imageLoading: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    color: '#6c757d',
    zIndex: 1
  },
  modalImageLoading: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    color: '#6c757d',
    zIndex: 1
  },
  hero: {
    background: "#6a2d57",
    padding: "115px 0",
    textAlign: "center",
  },
  heroTitle: {
    color: "#ff8b5a",
    fontSize: 40,
    margin: 0,
    fontWeight: 700,
    fontFamily: "'Poppins', sans-serif", // unified heading font
  },
  heroDescription: {
    fontSize: "1.1rem",
    fontWeight: 500,
    color: "#ffffff",
    maxWidth: "950px",
    margin: "0 auto",
    lineHeight: "1.6",
    opacity: 0.95,
    fontFamily: "'Poppins', sans-serif", // keep heading feel consistent
  },
  // Compact simple header for homepage (white bg)
  simpleHero: {
    background: "#ffffff",
    padding: "30px 0",
    textAlign: "center",
    borderBottom: "1px solid #eee",
  },
  simpleHeroTitle: {
    color: "#ff8b5a",
    fontSize: 36,
    margin: 0,
    fontWeight: 700,
    letterSpacing: 0.2,
    fontFamily: "'Poppins', sans-serif", // unified heading font
  },
  simpleHeroSubtitle: {
    color: "#6c757d",
    marginTop: 6,
    fontSize: 14,
    fontWeight: 500
  }
};

export default ProductPage;
